import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './VehicleSalesChat.css';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  isWaitingForResponse?: boolean;
  toolCalls?: Array<{ tool: string; status: 'pending' | 'complete' }>;
}

export const VehicleSalesChat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const generateId = () => {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const buildConversationHistory = (msgs: Message[]) => {
    return msgs
      .filter(m => !m.isStreaming) // Don't include incomplete messages
      .map(m => ({
        role: m.role,
        content: m.content
      }));
  };

  const sendMessage = async (question: string) => {
    if (!question.trim()) return;

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

    // Add user message
    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content: question,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    // Add assistant message (empty, will stream)
    const assistantMessageId = generateId();
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
      isWaitingForResponse: true,
      toolCalls: []
    };
    setMessages(prev => [...prev, assistantMessage]);

    setIsStreaming(true);

    try {
      // Create abort controller for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const response = await fetch(`${API_BASE_URL}/vehicle-sales/analyze-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          userQuestion: question,
          conversationHistory: buildConversationHistory(messages),
          filters: {}
        }),
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No reader available');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log('Stream complete');
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');

        // Keep the last incomplete line in the buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'chunk') {
                // Append text chunk to assistant message
                setMessages(prev => prev.map(m =>
                  m.id === assistantMessageId
                    ? { ...m, content: m.content + data.content, isWaitingForResponse: false }
                    : m
                ));
              }

              if (data.type === 'tool_use') {
                // Show tool being called
                console.log('🔧 Tool called:', data.tool);
                setMessages(prev => prev.map(m =>
                  m.id === assistantMessageId
                    ? {
                      ...m,
                      toolCalls: [...(m.toolCalls || []), { tool: data.tool, status: 'pending' }]
                    }
                    : m
                ));
              }

              if (data.type === 'tool_result') {
                // Mark tool as complete
                console.log('✅ Tool result received');
                setMessages(prev => prev.map(m =>
                  m.id === assistantMessageId && m.toolCalls
                    ? {
                      ...m,
                      toolCalls: m.toolCalls.map((tc, idx) =>
                        idx === m.toolCalls!.length - 1
                          ? { ...tc, status: 'complete' }
                          : tc
                      )
                    }
                    : m
                ));
              }

              if (data.type === 'complete') {
                // Mark message as complete
                setMessages(prev => prev.map(m =>
                  m.id === assistantMessageId
                    ? { ...m, isStreaming: false }
                    : m
                ));
                setIsStreaming(false);
              }

              if (data.type === 'error') {
                // Show error
                setMessages(prev => prev.map(m =>
                  m.id === assistantMessageId
                    ? {
                      ...m,
                      content: m.content + `\n\n❌ Error: ${data.error}`,
                      isStreaming: false
                    }
                    : m
                ));
                setIsStreaming(false);
              }
            } catch (parseError) {
              console.error('Error parsing SSE data:', parseError);
            }
          }
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('Request aborted');
      } else {
        console.error('Streaming error:', error);
        setMessages(prev => prev.map(m =>
          m.id === assistantMessageId
            ? {
              ...m,
              content: m.content + `\n\n❌ Error: ${error.message}`,
              isStreaming: false
            }
            : m
        ));
      }
      setIsStreaming(false);
    } finally {
      abortControllerRef.current = null;
    }
  };

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsStreaming(false);
    }
  };

  const clearConversation = () => {
    setMessages([]);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
      setInput('');
    }
  };

  return (
    <div className="vehicle-sales-chat">
      <div className="chat-header">
        <div className="header-content">
          <h2>🤖 Vehicle Sales AI Assistant</h2>
          <p>Ask questions about your sales data - I'll query the database in real-time</p>
        </div>
        <button onClick={clearConversation} className="clear-button" disabled={isStreaming}>
          🗑️ Clear
        </button>
      </div>

      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="welcome-screen">
            <div className="welcome-icon">💬</div>
            <h3>Welcome to Vehicle Sales Analysis</h3>
            <p>I can help you analyze your sales data. Try asking:</p>
            <div className="suggestion-chips">
              <button onClick={() => { sendMessage("Which state has the highest sales?"); }}>
                🗺️ Which state has highest sales?
              </button>
              <button onClick={() => { sendMessage("Top 10 dealers by revenue"); }}>
                🏆 Top 10 dealers by revenue
              </button>
              <button onClick={() => { sendMessage("Compare Q3 2024 vs Q3 2023"); }}>
                📊 Compare Q3 2024 vs Q3 2023
              </button>
              <button onClick={() => { sendMessage("Most profitable models"); }}>
                💰 Most profitable models
              </button>
            </div>
          </div>
        )}

        {messages.map(message => (
          <div key={message.id} className={`message ${message.role}`}>
            <div className="message-avatar">
              {message.role === 'user' ? '👤' : '🤖'}
            </div>
            <div className="message-bubble">
              <div className="message-content">
                {message.isWaitingForResponse ? (
                  <div className="thinking-indicator">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </div>
                ) : (
                  <>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                    {message.isStreaming && <span className="cursor">▊</span>}
                  </>
                )}
              </div>
              {message.toolCalls && message.toolCalls.length > 0 && (
                <div className="tool-calls">
                  {message.toolCalls.map((tc, idx) => (
                    <div key={idx} className={`tool-call ${tc.status}`}>
                      {tc.status === 'pending' ? '⏳' : '✅'} {tc.tool}
                    </div>
                  ))}
                </div>
              )}
              <div className="message-timestamp">
                {formatTime(message.timestamp)}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-container">
        <div className="chat-input">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question about your sales data..."
            disabled={isStreaming}
            rows={1}
          />
          {isStreaming ? (
            <button onClick={handleStop} className="stop-button">
              ⏹️
            </button>
          ) : (
            <button
              onClick={() => { sendMessage(input); setInput(''); }}
              disabled={!input.trim()}
              className="send-button"
            >
              ➤
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

